import React from "react";
import {
  Sparkles, Lock, CheckCircle2, AlertTriangle, XCircle, ArrowRight,
  Gauge, KeyRound, ListChecks, FileText, ShieldCheck, Download,
} from "lucide-react";
import { Button } from "../ui/button";

const STATUS_ICON = {
  good: { Icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50" },
  warning: { Icon: AlertTriangle, cls: "text-amber-600 bg-amber-50" },
  critical: { Icon: XCircle, cls: "text-rose-600 bg-rose-50" },
};

const PRIORITY_CLS = {
  high: "bg-rose-50 text-rose-700 border-rose-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};

const ScoreRing = ({ score = 0, grade }) => {
  const radius = 52;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;
  const color = score >= 80 ? "#059669" : score >= 55 ? "#d97706" : "#e11d48";
  return (
    <div className="relative h-36 w-36 shrink-0">
      <svg className="h-36 w-36 -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="10" />
        <circle
          cx="60" cy="60" r={radius} fill="none" stroke={color} strokeWidth="10"
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 1s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-4xl font-extrabold text-slate-900">{score}</span>
        <span className="text-xs font-semibold text-slate-500">Grade {grade}</span>
      </div>
    </div>
  );
};

const Section = ({ icon: Icon, title, children, right }) => (
  <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6 sm:p-8">
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </span>
        <h3 className="font-display text-xl font-bold text-slate-900">{title}</h3>
      </div>
      {right}
    </div>
    {children}
  </div>
);

const ReportOverview = ({ report, onDownload }) => (
  <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6 sm:p-8">
    <div className="flex flex-col md:flex-row items-center gap-8">
      <ScoreRing score={report.score || 0} grade={report.grade} />
      <div className="flex-1 text-center md:text-left">
        <div className="flex items-center justify-center md:justify-start gap-2 text-sm text-slate-500">
          <Gauge className="h-4 w-4 text-primary" /> Overall SEO Health
        </div>
        <p className="mt-2 text-slate-700 leading-relaxed">{report.summary}</p>
        <div className="mt-4 flex flex-wrap justify-center md:justify-start gap-2 text-xs">
          <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-600">{report.url}</span>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-600">{report.pages_crawled} page(s) crawled</span>
          {report.is_paid && (
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-emerald-700 flex items-center gap-1" data-testid="report-unlocked-badge">
              <ShieldCheck className="h-3.5 w-3.5" /> Full report unlocked forever
            </span>
          )}
          {report.pass_until && (
            <span className="rounded-full bg-primary/10 px-3 py-1 text-primary flex items-center gap-1" data-testid="active-pass-badge">
              <Sparkles className="h-3.5 w-3.5" /> New reports unlocked until {new Date(report.pass_until).toLocaleDateString()}
            </span>
          )}
        </div>
        {report.is_paid && (
          <Button onClick={onDownload} className="mt-4 rounded-lg bg-primary hover:bg-primary/90 shadow-md shadow-primary/20" data-testid="download-report-button">
            <Download className="h-4 w-4 mr-2" /> Download Report
          </Button>
        )}
      </div>
    </div>
  </div>
);

const AuditFindings = ({ audit = [] }) => (
  <Section icon={ListChecks} title="On-Page Audit">
    <div className="grid gap-3 sm:grid-cols-2">
      {audit.map((a) => {
        const meta = STATUS_ICON[a.status] || STATUS_ICON.warning;
        return (
          <div key={a.category} className="flex items-start gap-3 rounded-xl border border-slate-100 p-4">
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${meta.cls}`}>
              <meta.Icon className="h-4 w-4" />
            </span>
            <div>
              <p className="font-semibold text-slate-900 text-sm">{a.category}</p>
              <p className="text-sm text-slate-500 mt-0.5">{a.finding}</p>
            </div>
          </div>
        );
      })}
    </div>
  </Section>
);

const KeywordSection = ({ keywords = [] }) => (
  <Section icon={KeyRound} title="Keyword Opportunities">
    <div className="grid gap-3 sm:grid-cols-2">
      {keywords.map((k) => (
        <div key={k.keyword} className="rounded-xl border border-slate-100 p-4">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-slate-900 text-sm">{k.keyword}</p>
            <span className="text-[11px] rounded-full bg-slate-100 px-2 py-0.5 text-slate-600 capitalize">{k.difficulty}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary capitalize">{k.intent}</span>
            {k.cluster && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-500">{k.cluster}</span>}
          </div>
        </div>
      ))}
    </div>
  </Section>
);

const PremiumSections = ({ report }) => (
  <div className="space-y-6">
    <Section icon={FileText} title="AI-Optimised Meta Tags">
      <div className="space-y-4">
        <div className="rounded-xl border border-slate-100 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Title Tag</p>
          <p className="mt-1 text-slate-900 font-medium">{report.meta_suggestions?.title}</p>
        </div>
        <div className="rounded-xl border border-slate-100 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Meta Description</p>
          <p className="mt-1 text-slate-700">{report.meta_suggestions?.meta_description}</p>
        </div>
      </div>
    </Section>

    <Section icon={Sparkles} title="Content Suggestions">
      <ul className="space-y-2.5">
        {(report.content_suggestions || []).map((c) => (
          <li key={c} className="flex items-start gap-3 text-slate-700">
            <CheckCircle2 className="h-5 w-5 text-primary shrink-0" /> {c}
          </li>
        ))}
      </ul>
    </Section>

    <Section icon={ListChecks} title="Prioritised Fixes">
      <div className="space-y-3">
        {(report.fixes || []).map((f) => (
          <div key={f.title} className="rounded-xl border border-slate-100 p-4">
            <div className="flex items-center gap-2">
              <span className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 capitalize ${PRIORITY_CLS[f.priority] || PRIORITY_CLS.low}`}>
                {f.priority}
              </span>
              <p className="font-semibold text-slate-900 text-sm">{f.title}</p>
            </div>
            <p className="mt-1.5 text-sm text-slate-500">{f.detail}</p>
          </div>
        ))}
      </div>
    </Section>
  </div>
);

const SkeletonRows = ({ count = 4, rowClass = "h-14" }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    {Array.from({ length: count }, (_, n) => `sk-${n}`).map((k) => (
      <div key={k} className={`${rowClass} rounded-xl bg-slate-100`} />
    ))}
  </div>
);

const LockedCountBadge = ({ count, label }) =>
  count > 0 ? (
    <span className="text-xs font-semibold text-primary flex items-center gap-1">
      <Lock className="h-3.5 w-3.5" /> {count} {label} locked
    </span>
  ) : null;

const TeaserCard = ({ teaser }) => {
  const TeaserIcon = (STATUS_ICON[teaser.status] || STATUS_ICON.warning).Icon;
  const cls = teaser.status === "critical"
    ? "border-rose-200 bg-rose-50/95 text-rose-600"
    : "border-amber-200 bg-amber-50/95 text-amber-600";
  return (
    <div className={`mt-3 w-full max-w-md rounded-xl border-2 p-4 text-left shadow-sm ${cls}`} data-testid="teaser-finding-card">
      <div className="flex items-start gap-3">
        <TeaserIcon className="h-5 w-5 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-slate-900 text-sm">
            {teaser.category}
            <span className="ml-2 text-[11px] font-semibold rounded-full bg-white/80 px-2 py-0.5 capitalize">{teaser.status}</span>
          </p>
          <p className="text-sm text-slate-600 mt-0.5">{teaser.finding}</p>
        </div>
      </div>
    </div>
  );
};

const buildHook = (teaser) => {
  if (!teaser) return "Full report is locked";
  if (teaser.critical_count > 0) {
    return `Your site has ${teaser.critical_count} critical issue${teaser.critical_count > 1 ? "s" : ""}`;
  }
  return `Your site has ${teaser.warning_count} warning${teaser.warning_count > 1 ? "s" : ""} to fix`;
};

const LockedSkeletonSections = ({ counts }) => (
  <div className="pointer-events-none select-none opacity-60 space-y-6" aria-hidden="true">
    <Section icon={ListChecks} title="On-Page Audit" right={<LockedCountBadge count={counts.audit} label="findings" />}>
      <SkeletonRows count={6} />
    </Section>
    <Section icon={KeyRound} title="Keyword Opportunities" right={<LockedCountBadge count={counts.keywords} label="keywords" />}>
      <SkeletonRows count={4} />
    </Section>
    <Section icon={FileText} title="AI-Optimised Meta Tags">
      <div className="space-y-3">
        <div className="h-14 rounded-xl bg-slate-100" />
        <div className="h-14 rounded-xl bg-slate-100" />
      </div>
    </Section>
    <Section icon={ListChecks} title="Prioritised Fixes" right={<LockedCountBadge count={counts.fixes} label="fixes" />}>
      <SkeletonRows count={4} rowClass="h-12" />
    </Section>
  </div>
);

// Blurred placeholder for everything below "Overall SEO Health" until payment
const LockedReportPreview = ({ report, onUnlock }) => {
  const counts = report.locked_counts || {};
  const teaser = report.teaser;
  return (
    <div className="relative" data-testid="locked-report-preview">
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-2xl bg-white/60 backdrop-blur-[7px] text-center px-6">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Lock className="h-6 w-6" />
        </span>
        <p className="mt-3 font-display text-xl font-bold text-slate-900" data-testid="locked-hook-title">{buildHook(teaser)}</p>
        {teaser && <TeaserCard teaser={teaser} />}
        <p className="mt-3 text-sm text-slate-500 max-w-sm">
          Unlock to see all {counts.audit || ""} findings, {counts.keywords || ""} keyword opportunities,
          AI meta tags, content ideas and {counts.fixes || ""} prioritised fixes.
        </p>
        <Button onClick={onUnlock} className="mt-4 rounded-lg bg-primary hover:bg-primary/90" data-testid="locked-overlay-unlock-button">
          Unlock full access — $19.99
        </Button>
      </div>
      <LockedSkeletonSections counts={counts} />
    </div>
  );
};

const UnlockBanner = ({ report, paying, onUnlock }) => (
  <div className="rounded-2xl bg-slate-900 text-white p-8 text-center relative overflow-hidden">
    <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-primary/20 blur-3xl" />
    <div className="relative">
      <h3 className="font-display text-2xl font-extrabold">Unlock your full optimisation plan</h3>
      <p className="mt-2 text-slate-300 max-w-xl mx-auto">
        One payment unlocks this report forever — plus every new report you run for the next 30 days.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        {(report.plans || []).map((p) => (
          <button
            key={p.id}
            onClick={() => onUnlock(p.id)}
            disabled={paying}
            data-testid="cta-unlock-button"
            className="group rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 px-8 py-4 text-left transition-colors disabled:opacity-60"
          >
            <span className="block text-sm text-slate-300">{p.label}</span>
            <span className="mt-1 block font-display text-2xl font-extrabold">
              ${p.price?.toFixed(2)} <span className="text-xs font-semibold text-slate-400">{p.currency?.toUpperCase()} one-time</span>
            </span>
            <span className="mt-1 block text-xs text-primary flex items-center gap-1">
              {paying ? "Redirecting…" : "Unlock now"} <ArrowRight className="h-3 w-3" />
            </span>
          </button>
        ))}
      </div>
      <p className="mt-4 text-xs text-slate-400">Secure one-time payment via Stripe. Includes 30 days of unlimited new reports.</p>
    </div>
  </div>
);

export const ReportView = ({ report, paying, onUnlock, onDownload, onShowPlans }) => (
  <section className="pb-24" data-testid="report-view-section">
    <div className="container-wide space-y-6">
      <ReportOverview report={report} onDownload={onDownload} />
      {report.is_paid ? (
        <>
          <AuditFindings audit={report.audit || []} />
          <KeywordSection keywords={report.keywords || []} />
          <PremiumSections report={report} />
        </>
      ) : (
        <>
          <LockedReportPreview report={report} onUnlock={onShowPlans} />
          <UnlockBanner report={report} paying={paying} onUnlock={onUnlock} />
        </>
      )}
    </div>
  </section>
);
